import { defineAsyncComponent, getCurrentInstance } from 'vue';
import { normalizeVueformLocale, syncVueformLocale } from '../utils/vueformLocale';

const applications = new WeakMap();
let runtimePromise;

const stateFor = (app) => {
    if (!applications.has(app)) {
        applications.set(app, { locale: 'it', pending: null });
    }
    return applications.get(app);
};

export const preloadVueform = () => {
    // SSR installs its existing lightweight form stubs; never load browser fields there.
    if (import.meta.env.SSR) return Promise.resolve(null);

    runtimePromise ??= import('./vueformRuntime').catch((error) => {
        runtimePromise = null;
        throw error;
    });
    return runtimePromise;
};

export const setVueformLocale = (app, locale) => {
    stateFor(app).locale = normalizeVueformLocale(locale);
    return syncVueformLocale(app, locale);
};

export const ensureVueformReady = (app) => {
    if (import.meta.env.SSR || app.config.globalProperties.$vueform) {
        return Promise.resolve();
    }

    const state = stateFor(app);
    state.pending ??= preloadVueform().then((runtime) => {
        runtime.installVueform(app, state.locale);
        syncVueformLocale(app, state.locale);
    }).catch((error) => {
        state.pending = null;
        throw error;
    });
    return state.pending;
};

// Call in the parent's setup. The complete form subtree mounts only after the
// plugin has registered its fields, preserving slots, events and component refs.
export const useVueformComponent = (importer) => {
    const app = getCurrentInstance()?.appContext.app;
    if (!app) throw new Error('useVueformComponent must be called during setup.');

    return defineAsyncComponent(async () => {
        const [component] = await Promise.all([importer(), ensureVueformReady(app)]);
        return component.default ?? component;
    });
};
